import React, { useCallback, useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Box,
  Typography,
  CircularProgress,
  InputAdornment,
  IconButton,
  Card,
  CardContent,
  Stack,
  Divider,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Search as SearchIcon, Close as CloseIcon } from '@mui/icons-material';
import productoService, {
  OPTIONS_PRODUCTO_OPERATIVO,
  PARAMS_PRODUCTO_OPERATIVO,
  Producto,
} from '../services/productoService';

interface ProductoSearchProps {
  open: boolean;
  onClose: () => void;
  onSelect: (producto: Producto) => void;
  /** Si true, no cierra al seleccionar (útil para agregar varios rápido). */
  keepOpenOnSelect?: boolean;
  /** Códigos ya elegidos: se marcan en la lista. */
  selectedCodigos?: string[];
}

const PAGE_SIZE = 25;

const ProductoSearch: React.FC<ProductoSearchProps> = ({
  open,
  onClose,
  onSelect,
  keepOpenOnSelect = false,
  selectedCodigos = [],
}) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [searchTerm, setSearchTerm] = useState('');
  const [productos, setProductos] = useState<Producto[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const selectedSet = new Set(selectedCodigos.map((c) => c.trim().toUpperCase()).filter(Boolean));

  const handleSearch = useCallback(
    async (pageNumber = page) => {
      try {
        setLoading(true);
        setError(null);
        const trimmed = searchTerm.trim();
        const compact = trimmed.replace(/\s+/g, '');

        if (compact && pageNumber === 0) {
          try {
            const exacto = await productoService.getProducto(compact, OPTIONS_PRODUCTO_OPERATIVO);
            if (exacto?.codigoArticulo) {
              setProductos([exacto]);
              setTotalCount(1);
              return;
            }
          } catch {
            /* buscar por texto */
          }
        }

        const response = await productoService.getProductos({
          searchTerm: trimmed || undefined,
          pageNumber: pageNumber + 1,
          pageSize: PAGE_SIZE,
          ...PARAMS_PRODUCTO_OPERATIVO,
        });
        setProductos(response.items || []);
        setTotalCount(response.totalCount ?? 0);
        if (!response.items || response.items.length === 0) {
          setError('No se encontraron artículos.');
        }
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Error desconocido';
        setError(`Error al buscar artículos: ${message}`);
        setProductos([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    },
    [page, searchTerm],
  );

  const handleClose = () => {
    setSearchTerm('');
    setProductos([]);
    setError(null);
    setPage(0);
    onClose();
  };

  const handleSelect = (producto: Producto) => {
    onSelect(producto);
    if (!keepOpenOnSelect) {
      handleClose();
      return;
    }
    // En modo multi: limpiar búsqueda para el próximo pick sin cerrar
    setSearchTerm('');
    setPage(0);
  };

  useEffect(() => {
    if (!open) {
      setSearchTerm('');
      setProductos([]);
      setError(null);
      setPage(0);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handle = setTimeout(() => {
      void handleSearch(page);
    }, searchTerm.trim() ? 180 : 0);
    return () => clearTimeout(handle);
  }, [open, searchTerm, page, handleSearch]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      fullScreen={isSmallScreen}
      sx={{
        '& .MuiDialog-paper': {
          margin: { xs: 0, sm: 2 },
          maxHeight: { xs: '100%', sm: '90vh' },
          height: { xs: '100%', sm: 'auto' },
        },
      }}
    >
      <DialogTitle sx={{ pb: { xs: 1, sm: 2 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
            Buscar artículo
            {keepOpenOnSelect && selectedCodigos.length > 0
              ? ` (${selectedCodigos.length} seleccionado${selectedCodigos.length === 1 ? '' : 's'})`
              : ''}
          </Typography>
          <IconButton onClick={handleClose} size="small" aria-label="Cerrar">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 1, sm: 2 } }}>
        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            autoFocus
            variant="outlined"
            placeholder="Código o descripción"
            inputProps={{ 'aria-label': 'Buscar artículo' }}
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(0);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                void handleSearch(0);
              }
            }}
            size={isSmallScreen ? 'small' : 'medium'}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {loading ? (
                    <CircularProgress size={18} />
                  ) : (
                    <IconButton onClick={() => void handleSearch(page)} disabled={loading} edge="end" aria-label="Buscar">
                      <SearchIcon />
                    </IconButton>
                  )}
                </InputAdornment>
              ),
            }}
            sx={{ mb: 1.5 }}
          />
          <Box sx={{ display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
            <Button
              variant="outlined"
              onClick={() => {
                setPage(0);
                void handleSearch(0);
              }}
              disabled={loading}
              size={isSmallScreen ? 'medium' : 'small'}
              fullWidth={isSmallScreen}
              startIcon={<SearchIcon />}
            >
              BUSCAR
            </Button>
            <Button
              variant="text"
              onClick={() => {
                setSearchTerm('');
                setPage(0);
              }}
              disabled={loading}
              size={isSmallScreen ? 'medium' : 'small'}
              fullWidth={isSmallScreen}
            >
              VER TODOS
            </Button>
          </Box>
        </Box>

        {error && (
          <Typography color="error" sx={{ mb: 2, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
            {error}
          </Typography>
        )}

        {loading && productos.length === 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        )}

        {productos.length > 0 && (
          <>
            {isSmallScreen ? (
              <Box sx={{ maxHeight: '60vh', overflowY: 'auto', pr: 1 }}>
                <Stack spacing={2}>
                  {productos.map((producto) => {
                    const codigo = String(producto.codigoArticulo ?? '').trim();
                    const yaAgregado = selectedSet.has(codigo.toUpperCase());
                    return (
                      <Card
                        key={codigo}
                        sx={{
                          cursor: yaAgregado ? 'default' : 'pointer',
                          opacity: yaAgregado ? 0.7 : 1,
                          '&:hover': yaAgregado ? undefined : { boxShadow: 3 },
                        }}
                        onClick={() => {
                          if (!yaAgregado) handleSelect(producto);
                        }}
                      >
                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                            <Typography variant="subtitle2" color="primary" fontWeight="bold">
                              Código: {codigo}
                            </Typography>
                            <Button
                              variant="contained"
                              size="small"
                              disabled={yaAgregado}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!yaAgregado) handleSelect(producto);
                              }}
                              sx={{ minWidth: 'auto', px: 2 }}
                            >
                              {yaAgregado ? 'Agregado' : keepOpenOnSelect ? 'Agregar' : 'Seleccionar'}
                            </Button>
                          </Box>
                          <Divider sx={{ my: 1.5 }} />
                          <Typography variant="caption" color="text.secondary" display="block">
                            Descripción
                          </Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {producto.descripcion?.trim() || '-'}
                          </Typography>
                        </CardContent>
                      </Card>
                    );
                  })}
                </Stack>
              </Box>
            ) : (
              <TableContainer
                component={Paper}
                sx={{
                  maxHeight: 400,
                  '& .MuiTableCell-root': { py: 1.5, fontSize: '0.875rem' },
                }}
              >
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', minWidth: 150 }}>Código</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', minWidth: 300 }}>Descripción</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', width: 120 }} align="center">
                        Acción
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {productos.map((producto) => {
                      const codigo = String(producto.codigoArticulo ?? '').trim();
                      const yaAgregado = selectedSet.has(codigo.toUpperCase());
                      return (
                        <TableRow
                          key={codigo}
                          hover={!yaAgregado}
                          selected={yaAgregado}
                          sx={{ cursor: yaAgregado ? 'default' : 'pointer' }}
                          onClick={() => {
                            if (!yaAgregado) handleSelect(producto);
                          }}
                        >
                          <TableCell>{codigo}</TableCell>
                          <TableCell>{producto.descripcion?.trim() || '-'}</TableCell>
                          <TableCell align="center">
                            <Button
                              variant={yaAgregado ? 'contained' : 'outlined'}
                              color={yaAgregado ? 'success' : 'primary'}
                              size="small"
                              disabled={yaAgregado}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!yaAgregado) handleSelect(producto);
                              }}
                            >
                              {yaAgregado ? 'Agregado' : keepOpenOnSelect ? 'Agregar' : 'Seleccionar'}
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={(_e, p) => setPage(p)}
              rowsPerPage={PAGE_SIZE}
              rowsPerPageOptions={[PAGE_SIZE]}
              labelRowsPerPage="Filas"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            />
          </>
        )}

        {!loading && productos.length === 0 && searchTerm && !error && (
          <Typography color="text.secondary" sx={{ textAlign: 'center', p: 3 }}>
            No se encontraron artículos
          </Typography>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: { xs: 2, sm: 3 },
          pb: { xs: 2, sm: 2 },
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 1,
        }}
      >
        <Button
          onClick={handleClose}
          fullWidth={isSmallScreen}
          size={isSmallScreen ? 'medium' : 'small'}
          variant={keepOpenOnSelect ? 'contained' : 'outlined'}
        >
          {keepOpenOnSelect ? 'LISTO' : 'CANCELAR'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProductoSearch;
